import { Role } from '@mai-team-amusement/core';
import {
  LocaleProvider,
  MaiApp,
  SystemLayout,
  useCommonT,
  type SystemNavItem,
} from '@mai-team-amusement/ui';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { i18n } from 'i18next';
import { RouterProvider, createBrowserRouter, type RouteObject } from 'react-router';
import { useCapabilities } from './capabilities.js';
import { useAssetT } from './i18n/index.js';
import { MyAssetsPage } from './pages/MyAssetsPage.js';

const LOADING_MARGIN = 8;

function Layout() {
  const t = useAssetT();
  const common = useCommonT();
  const capabilities = useCapabilities();
  // Asset manager rights live in this system, so tabs that need them are filtered here; the
  // layout itself filters by role only.
  const navigation: SystemNavItem[] = [
    { path: '/', label: t('nav.myAssets'), minRole: Role.Employee },
    ...(capabilities.canListAssets
      ? [{ path: '/assets', label: t('nav.assets'), minRole: Role.Employee }]
      : []),
    ...(capabilities.canManageAssets
      ? [
          { path: '/inventories', label: t('nav.inventories'), minRole: Role.Employee },
          { path: '/import', label: t('nav.import'), minRole: Role.Employee },
          { path: '/locations', label: t('nav.locations'), minRole: Role.Employee },
        ]
      : []),
    ...(capabilities.canAdminister
      ? [{ path: '/admin', label: common('system.nav.admin'), minRole: Role.Admin }]
      : []),
  ];
  return <SystemLayout app={MaiApp.FixedAsset} navigation={navigation} />;
}

/** Shown while the first page's code loads. */
function RouteLoading() {
  const common = useCommonT();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: LOADING_MARGIN }}>
      <CircularProgress aria-label={common('status.loading')} />
    </Box>
  );
}

/**
 * The app's routes (also rendered by tests with a memory router). "My assets" is in the main
 * bundle; the other pages load on first use. A new array per router: resolving a lazy route
 * updates its route object.
 */
export const createRoutes = (): RouteObject[] => [
  {
    path: '/',
    Component: Layout,
    HydrateFallback: RouteLoading,
    children: [
      { index: true, Component: MyAssetsPage },
      {
        path: 'assets',
        lazy: { Component: async () => (await import('./pages/AssetsPage.js')).AssetsPage },
      },
      {
        path: 'assets/new',
        lazy: { Component: async () => (await import('./pages/NewAssetPage.js')).NewAssetPage },
      },
      {
        path: 'assets/:code',
        lazy: { Component: async () => (await import('./pages/AssetPage.js')).AssetPage },
      },
      {
        path: 'a/:code',
        lazy: { Component: async () => (await import('./pages/QrCardPage.js')).QrCardPage },
      },
      {
        path: 'inventories',
        lazy: {
          Component: async () => (await import('./pages/InventoriesPage.js')).InventoriesPage,
        },
      },
      {
        path: 'inventories/:id',
        lazy: { Component: async () => (await import('./pages/InventoryPage.js')).InventoryPage },
      },
      {
        path: 'import',
        lazy: { Component: async () => (await import('./pages/ImportPage.js')).ImportPage },
      },
      {
        path: 'labels',
        lazy: { Component: async () => (await import('./pages/LabelsPage.js')).LabelsPage },
      },
      {
        path: 'locations',
        lazy: { Component: async () => (await import('./pages/LocationsPage.js')).LocationsPage },
      },
      {
        path: 'admin',
        lazy: { Component: async () => (await import('./pages/AdminPage.js')).AdminPage },
      },
    ],
  },
];

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

const router = createBrowserRouter(createRoutes());

export function App({ i18n }: { i18n: i18n }) {
  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider i18n={i18n}>
        <RouterProvider router={router} />
      </LocaleProvider>
    </QueryClientProvider>
  );
}
